import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";

// ✅ IMPORTAR COMPONENTES
import Notifications from "../components/Notifications";
import LogConsole from "../components/LogConsole";
import Balances from "../components/Balances";
import OrdersTable from "../components/OrdersTable";
import MarketStateBadges, { JornadaResumen } from "../components/MarketStateBadges";
import Indicators from "../components/Indicators";
import IndicatorsChart from "../components/IndicatorsChart";
import MultiPairChart from "../components/MultiPairChart";
import OrderHistory from "../components/OrderHistory";
import LiveBlocks from "../components/LiveBlocks";
import NewsCarousel from "../components/NewsCarousel";
import Rendimientos from "../components/Rendimientos";
import AddUserModal from "../components/AddUserModal";
import AdminUsersOverview from "../components/AdminUsersOverview";
import PreregistrosOverview from "../components/PreregistrosOverview";
import ApprovePreregModal from "../components/ApprovePreregModal";
import EditUserModal from "../components/EditUserModal";
import UserLogModal from "../components/UserLogModal";
import { normalizeTradingPairs } from "../components/TradingPairsSelector";
import { storage } from "../services/storage";

import { wsManager } from "../services/WebSocketData";
import {
  createAdminUser,
  approvePreregistro,
  applyAdminUserPairSuggestions,
  deletePreregistro,
  deleteAdminUser,
  getAdminPreregistros,
  getAdminUsersOverview,
  getActiveOrders,
  getAdminActiveOrders,
  getAdminUsers,
  getBalances,
  getCurrentUser,
  getIndicators,
  getNewsState,
  getSymbolLiveLog,
  getTradingPairsStatus,
  logoutUser,
  markSubscriptionPaid,
  resendAdminUserAccess,
  restartAdminUserBot,
  sellActiveOrder,
  startAdminUserBot,
  stopAdminUserBot,
  updateAdminUser,
  updateCurrentUserPassword,
  updateCurrentUserProfile,
} from "../services/api";
import "../styles/Dashboard.css";

const LOG_BATCH_MS = 1500;
const MAX_LOG_LINES = 120;
const MAX_LOG_BUFFER = 160;
const MAX_LIVE_BLOCK_LINES = 900;
const REFRESH_BUMP_MIN_MS = 5000;
const ACTIVE_ORDERS_REFRESH_MIN_MS = 15000;
const ACTIVE_ORDERS_POLL_MS = 30000;
const PAIRS_STATUS_POLL_MS = 45000;
const PAIR_INDICATORS_BATCH_SIZE = 5;

const Dashboard = () => {
  const wsInitializedRef = useRef(false); // ✅ NUEVA REF PARA CONTROLAR INIT
  const ordersFetchInFlightRef = useRef(false);
  const logBufferRef = useRef([]);
  const logFlushTimerRef = useRef(null);
  const refreshBumpRef = useRef(0);
  const lastActiveOrdersFetchRef = useRef(0);
  // Ref que apunta al último pairMonitorRows (listeners/intervalos definidos antes de su cálculo).
  const pairMonitorRowsRef = useRef([]);
  const [activeOrders, setActiveOrders] = useState([]);
  const [activeOrdersUser, setActiveOrdersUser] = useState("ALL");
  const [balances, setBalances] = useState([]);
  const [balancesTotalUsdt, setBalancesTotalUsdt] = useState(null);
  const [balancesWallet, setBalancesWallet] = useState({ spotUsdt: null, futuresUsdt: null });
  const [notifications, setNotifications] = useState([]);
  const [logLines, setLogLines] = useState([]);
  const [liveBlocksLines, setLiveBlocksLines] = useState([]);
  const [symbolHistoryLogs, setSymbolHistoryLogs] = useState([]);
  const [newsState, setNewsState] = useState(null);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsError, setNewsError] = useState("");
  const [pairsStatus, setPairsStatus] = useState([]);
  const [pairsStatusLoading, setPairsStatusLoading] = useState(false);
  const [pairsStatusError, setPairsStatusError] = useState("");
  const [pairIndicators, setPairIndicators] = useState({});
  const [pairIndicatorsLoading, setPairIndicatorsLoading] = useState(false);
  const [pairIndicatorsError, setPairIndicatorsError] = useState("");
  const [pairIndicatorsMissing, setPairIndicatorsMissing] = useState([]);
  const [wsStatus, setWsStatus] = useState("DISCONNECTED");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [manualRefreshTrigger, setManualRefreshTrigger] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showConsole, setShowConsole] = useState(true);
  const [showLiveBlocks, setShowLiveBlocks] = useState(true);
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);
  const [showOrders, setShowOrders] = useState(true);
  const [showNews, setShowNews] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [adminOverview, setAdminOverview] = useState([]);
  const [adminOverviewLoading, setAdminOverviewLoading] = useState(false);
  const [adminOverviewError, setAdminOverviewError] = useState("");
  const [preregistros, setPreregistros] = useState([]);
  const [preregistrosLoading, setPreregistrosLoading] = useState(false);
  const [preregistrosError, setPreregistrosError] = useState("");
  const [preregistroBusyEmail, setPreregistroBusyEmail] = useState("");
  const [approvePreregItem, setApprovePreregItem] = useState(null);
  const [approvePreregState, setApprovePreregState] = useState({ loading: false, error: "", success: "" });
  const [editUser, setEditUser] = useState(null);
  const [editUserState, setEditUserState] = useState({ loading: false, error: "", success: "" });
  const [currentProfile, setCurrentProfile] = useState(null);
  const [activationWallet, setActivationWallet] = useState("");
  const [activationPionexRecipient, setActivationPionexRecipient] = useState("");
  const [activationPionexUid, setActivationPionexUid] = useState("");
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileSetupLocked, setProfileSetupLocked] = useState(false);
  const [selfProfileState, setSelfProfileState] = useState({ loading: false, error: "", success: "" });
  const [logModalUser, setLogModalUser] = useState(null);
  const [logModalState, setLogModalState] = useState({ loading: false, error: "", lines: [] });
  const logModalUserRef = useRef(null);
  const [addUserState, setAddUserState] = useState({ loading: false, error: "", success: "" });
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [adminUsersError, setAdminUsersError] = useState("");
  const [adminUsersBusy, setAdminUsersBusy] = useState({ resend: "", delete: "" });
  const [dashboardView, setDashboardView] = useState("BOTH");
  const [selectedSymbol, setSelectedSymbol] = useState(
    process.env.REACT_APP_DEFAULT_SYMBOL || "SOL_USDT"
  );
  const [tradeMode, setTradeMode] = useState("");
  const tradeModeRef = useRef("");
  const selectedSymbolRef = useRef("");
  const debugWs = process.env.REACT_APP_DEBUG_WS === "true";
  const NEWS_ENABLED = true;
  const NEWS_CAROUSEL_TAG = "2026-03-21-1";
  const showTradeView = true;

  useEffect(() => {
    tradeModeRef.current = String(tradeMode || "").toUpperCase();
  }, [tradeMode]);

  useEffect(() => {
    selectedSymbolRef.current = normalizeSymbol(selectedSymbol);
  }, [selectedSymbol]);


  const normalizeSymbol = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    const upper = raw.toUpperCase();
    if (upper.includes("_")) return upper;
    if (upper.endsWith("USDT")) {
      return upper.replace("USDT", "_USDT");
    }
    return upper;
  };

  const getPrimaryTradingSymbol = (user = {}) => {
    const pairs = Array.isArray(user?.trading_pairs) ? user.trading_pairs : [];
    const primaryPair = pairs.find((pair) => pair?.symbol || typeof pair === "string");
    const rawSymbol = typeof primaryPair === "string" ? primaryPair : primaryPair?.symbol;
    return normalizeSymbol(rawSymbol || user?.symbol || "");
  };

  const getTradingPairsLabel = (user = {}) => {
    const pairs = Array.isArray(user?.trading_pairs) ? user.trading_pairs : [];
    if (!pairs.length) return normalizeSymbol(user?.symbol) || selectedSymbol || "N/A";

    if (pairs.length === 1) {
      const pair = pairs[0];
      const symbol = normalizeSymbol(typeof pair === "string" ? pair : pair?.symbol);
      const market = String(
        typeof pair === "string" ? "" : pair?.market_type || pair?.market || ""
      ).trim().toUpperCase();
      const marketType = market || (symbol.endsWith("_PERP") ? "FUTURES" : "SPOT");
      return `${symbol} · ${marketType === "FUTURES" ? "Futuros" : "Spot"}`;
    }

    return `${pairs.length} pares`;
  };

  const getConfiguredPairs = (user = {}) => {
    const pairs = Array.isArray(user?.trading_pairs) ? user.trading_pairs : [];
    if (pairs.length) {
      return pairs
        .map((pair) => {
          const symbol = normalizeSymbol(typeof pair === "string" ? pair : pair?.symbol);
          if (!symbol) return null;
          const market = String(typeof pair === "string" ? "" : pair?.market_type || pair?.market || "")
            .trim()
            .toUpperCase();
          return {
            symbol,
            market_type: market || (symbol.endsWith("_PERP") ? "FUTURES" : "SPOT"),
          };
        })
        .filter(Boolean);
    }
    const symbol = normalizeSymbol(user?.symbol || selectedSymbol);
    return symbol ? [{ symbol, market_type: symbol.endsWith("_PERP") ? "FUTURES" : "SPOT" }] : [];
  };

  const normalizeDashboardView = (value) => {
    const raw = String(value || "").trim().toUpperCase();
    if (raw === "TRADE") return "TRADE";
    return "BOTH";
  };

  const toNumber = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  };

  const formatNumber = (value, digits = 2) => {
    const numeric = toNumber(value);
    return numeric === null ? "N/A" : numeric.toFixed(digits);
  };

  const formatPrice = (value) => {
    const numeric = toNumber(value);
    if (numeric === null) return "N/A";
    if (Math.abs(numeric) >= 100) return `$${numeric.toFixed(2)}`;
    if (Math.abs(numeric) >= 1) return `$${numeric.toFixed(4)}`;
    return `$${numeric.toFixed(6)}`;
  };

  const formatHzRemaining = (expirySeconds) => {
    const expiry = toNumber(expirySeconds);
    if (expiry === null || expiry <= 0) return null;
    const remainingS = expiry - Date.now() / 1000;
    if (!Number.isFinite(remainingS) || remainingS <= 0) return "vence en 0m";
    const days = Math.floor(remainingS / 86400);
    const hours = Math.floor((remainingS % 86400) / 3600);
    const mins = Math.floor((remainingS % 3600) / 60);
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0 || days > 0) parts.push(`${hours}h`);
    parts.push(`${mins}m`);
    return `vence en ${parts.join(" ")}`;
  };

  const getIndicatorReading = (indicator = {}) => {
    const trend = String(indicator?.trend || "").toUpperCase();
    const rsi = toNumber(indicator?.RSI);
    const macd = toNumber(indicator?.MACD);
    const macdSignal = toNumber(indicator?.MACD_signal);
    const close = toNumber(indicator?.close);
    const bbMiddle = toNumber(indicator?.BB_middle_1h_5m ?? indicator?.BB_middle);
    let score = 0;
    if (trend.includes("ALCISTA")) score += 35;
    else if (trend.includes("NEUTRAL")) score += 15;
    if (rsi !== null && rsi >= 35 && rsi <= 68) score += 25;
    else if (rsi !== null && ((rsi >= 28 && rsi < 35) || (rsi > 68 && rsi <= 74))) score += 10;
    if (macd !== null && macdSignal !== null && macd > macdSignal) score += 20;
    if (bbMiddle && close && close > bbMiddle) score += 20;
    if (score >= 75) return { label: "Compra alta", tone: "positive", score };
    if (score >= 52) return { label: "Compra media", tone: "warning", score };
    return { label: "Compra baja", tone: "negative", score };
  };

  const getOrderIdValue = (order) =>
    String(
      order?.orderId ??
        order?.order_id ??
        order?.id ??
        order?._id ??
        order?.mongo_id ??
        ""
    ).trim();

  const orderKey = (order, index) => {
    const id = getOrderIdValue(order);
    if (id) return id;
    const symbol = String(order?.symbol || "").trim().toUpperCase();
    return `${symbol || "NA"}:${index}`;
  };

  const normalizeOrderValue = (value) => {
    const numeric = toNumber(value);
    if (numeric === null) return String(value ?? "");
    return numeric.toFixed(8);
  };

  const containsCycleHeader = (message) => {
    const text = String(message || "");
    return /Inicio\s+(de\s+)?Ciclo/i.test(text);
  };

  const isSameOrder = (a, b) => {
    if (!a || !b) return false;
    if (getOrderIdValue(a) !== getOrderIdValue(b)) {
      return false;
    }
    const statusA = String(a?.estado || a?.status || "").toUpperCase();
    const statusB = String(b?.estado || b?.status || "").toUpperCase();
    if (statusA !== statusB) return false;
    const symbolA = String(a?.symbol || "").toUpperCase();
    const symbolB = String(b?.symbol || "").toUpperCase();
    if (symbolA !== symbolB) return false;
    const userA = String(a?.account_id || a?.user || "").toLowerCase();
    const userB = String(b?.account_id || b?.user || "").toLowerCase();
    if (userA !== userB) return false;

    const priceA = normalizeOrderValue(a?.precio_actual ?? a?.price);
    const priceB = normalizeOrderValue(b?.precio_actual ?? b?.price);
    if (priceA !== priceB) return false;

    const pnlPctA = normalizeOrderValue(a?.p_l_porcentaje);
    const pnlPctB = normalizeOrderValue(b?.p_l_porcentaje);
    if (pnlPctA !== pnlPctB) return false;

    const pnlA = normalizeOrderValue(a?.p_l_precio);
    const pnlB = normalizeOrderValue(b?.p_l_precio);
    if (pnlA !== pnlB) return false;

    const qtyA = normalizeOrderValue(a?.cantidad ?? a?.size);
    const qtyB = normalizeOrderValue(b?.cantidad ?? b?.size);
    if (qtyA !== qtyB) return false;

    return true;
  };

  const areOrdersEquivalent = (prev, next) => {
    if (!Array.isArray(prev) || !Array.isArray(next)) return false;
    if (prev.length !== next.length) return false;
    if (prev.length === 0) return true;

    const prevMap = new Map(prev.map((order, idx) => [orderKey(order, idx), order]));
    for (let i = 0; i < next.length; i += 1) {
      const nextOrder = next[i];
      const key = orderKey(nextOrder, i);
      const prevOrder = prevMap.get(key);
      if (!prevOrder) return false;
      if (!isSameOrder(prevOrder, nextOrder)) return false;
    }
    return true;
  };

  const normalizeNewsState = (payload) => {
    if (!payload || typeof payload !== "object") return null;
    return {
      ...payload,
      predicciones_recientes: Array.isArray(payload.predicciones_recientes)
        ? payload.predicciones_recientes
        : [],
    };
  };

  const buildOrderFromCompra = (data) => {
    const price = toNumber(data?.price);
    const size = toNumber(data?.size);
    const sl = toNumber(data?.sl);
    const tp = toNumber(data?.tp);
    const symbol = String(data?.symbol || selectedSymbol || "").trim().toUpperCase();
    const orderId = getOrderIdValue(data);
    const now = data?.timestamp || new Date().toISOString();
    const accountId = String(data?.account_id || data?.user || "");

    return {
      orderId,
      symbol: symbol || "N/A",
      estado: "ABIERTA",
      account_id: accountId,
      precio_compra: price,
      precio_actual: price,
      max_precio: price,
      cantidad: size,
      p_l_precio: 0,
      p_l_porcentaje: 0,
      p_l_color: "verde",
      sl_price: sl,
      sl_percentage: toNumber(data?.sl_pct) ?? (data?.sl_percentage != null ? toNumber(data.sl_percentage) : null),
      tp_price: tp,
      tp_percentage: toNumber(data?.tp_pct) ?? (data?.tp_percentage != null ? toNumber(data.tp_percentage) : null),
      rr_ratio: data?.rr_ratio ?? 0,
      entry_method: String(data?.entry_method || data?.method || ""),
      hz_horas: data?.hz_horas != null ? toNumber(data.hz_horas) : null,
      fecha_compra: now,
    };
  };

  const upsertOrder = (orders, nextOrder) => {
    const nextOrderId = getOrderIdValue(nextOrder);
    if (!nextOrderId) return orders;
    const existingIndex = orders.findIndex((item) => getOrderIdValue(item) === nextOrderId);
    if (existingIndex === -1) return [nextOrder, ...orders];
    const copy = [...orders];
    copy[existingIndex] = { ...copy[existingIndex], ...nextOrder, orderId: nextOrderId };
    return copy;
  };

  const applyOrderUpdate = (orders, data) => {
    const orderId = getOrderIdValue(data);
    if (!orderId) return orders;
    const precioActual = toNumber(data?.precio_actual ?? data?.price);
    const pnlPct = toNumber(data?.p_l_porcentaje ?? data?.pnl_percentage);
    const symbol = String(data?.symbol || "").trim().toUpperCase();
    const timestamp = data?.timestamp;
    const accountId = String(data?.account_id || data?.user || "");

    const existing = orders.find((item) => getOrderIdValue(item) === orderId);
    if (!existing) {
      return upsertOrder(orders, {
        orderId,
        symbol: symbol || "N/A",
        estado: "ABIERTA",
        account_id: accountId,
        precio_actual: precioActual,
        max_precio: precioActual,
        p_l_porcentaje: pnlPct,
        p_l_color: pnlPct === null ? undefined : pnlPct >= 0 ? "verde" : "rojo",
        p_l_precio: 0,
        fecha_compra: timestamp || new Date().toISOString(),
      });
    }

    return orders.map((order) => {
      if (getOrderIdValue(order) !== orderId) return order;
      const maxPrecio = toNumber(order?.max_precio);
      const nextMax =
        precioActual === null
          ? maxPrecio
          : Math.max(maxPrecio ?? 0, precioActual);
      const buyPrice = toNumber(order?.precio_compra);
      const size = toNumber(order?.cantidad);
      const pnlPrecio =
        buyPrice !== null && size !== null && precioActual !== null
          ? (precioActual - buyPrice) * size
          : order?.p_l_precio ?? 0;
      return {
        ...order,
        orderId,
        account_id: accountId || order.account_id,
        symbol: symbol || order.symbol,
        precio_actual: precioActual ?? order.precio_actual,
        max_precio: nextMax ?? order.max_precio,
        p_l_porcentaje: pnlPct ?? order.p_l_porcentaje,
        p_l_color:
          pnlPct === null
            ? order.p_l_color
            : pnlPct >= 0
            ? "verde"
            : "rojo",
        p_l_precio: pnlPrecio,
        fecha_compra: timestamp || order.fecha_compra,
      };
    });
  };

  const accountMatchesFilter = (payload, filterUser) => {
    if (!filterUser || filterUser === "ALL") return true;
    const payloadAccount = String(
      payload?.account_id || payload?.user || ""
    ).trim().toLowerCase();
    return payloadAccount === String(filterUser || "").trim().toLowerCase();
  };

  const flushLogBuffer = useCallback(() => {
    // No renderizar logs mientras la pestaña está en segundo plano.
    if (document.hidden) return;
    if (!logBufferRef.current.length) return;
    const pending = logBufferRef.current;
    logBufferRef.current = [];
    setLogLines((prev) => {
      const merged = [...prev, ...pending];
      return merged.slice(-MAX_LOG_LINES);
    });
    setLiveBlocksLines((prev) => {
      let next = [...prev];
      pending.forEach((entry) => {
        // Mantener historial reciente para evitar vacíos entre ciclos.
        next.push(entry);
      });
      if (next.length > MAX_LIVE_BLOCK_LINES) {
        next = next.slice(-MAX_LIVE_BLOCK_LINES);
      }
      return next;
    });
  }, []);

  const handleClearLogs = useCallback(() => {
    logBufferRef.current = [];
    setLogLines([]);
  }, []);

  const scheduleLogFlush = useCallback(() => {
    // No programar flushes mientras la pestaña está oculta; se hará al volver a primer plano.
    if (document.hidden) return;
    if (logFlushTimerRef.current) return;
    logFlushTimerRef.current = setTimeout(() => {
      logFlushTimerRef.current = null;
      flushLogBuffer();
    }, LOG_BATCH_MS);
  }, [flushLogBuffer]);

  const bumpRefreshTrigger = useCallback((minIntervalMs = REFRESH_BUMP_MIN_MS) => {
    const now = Date.now();
    if (now - refreshBumpRef.current < minIntervalMs) return;
    refreshBumpRef.current = now;
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  const requestActiveOrdersRefresh = useCallback((minIntervalMs = ACTIVE_ORDERS_REFRESH_MIN_MS) => {
    const now = Date.now();
    if (now - lastActiveOrdersFetchRef.current < minIntervalMs) return;
    lastActiveOrdersFetchRef.current = now;
    void fetchActiveOrders(true);
  }, []);

  useEffect(() => {
    return () => {
      if (logFlushTimerRef.current) {
        clearTimeout(logFlushTimerRef.current);
        logFlushTimerRef.current = null;
      }
    };
  }, []);

  const extractOrdersArray = (payload) => {
    if (!payload || typeof payload !== "object") return null;
    const directCandidates = [
      payload.orders,
      payload.ordenes,
      payload.active_orders,
      payload.ordenes_abiertas,
    ];
    const directList = directCandidates.find((candidate) => Array.isArray(candidate));
    if (directList) return directList;

    const nested = payload.data || payload.payload || payload.body;
    if (nested && typeof nested === "object") {
      const nestedCandidates = [
        nested.orders,
        nested.ordenes,
        nested.active_orders,
        nested.ordenes_abiertas,
      ];
      const nestedList = nestedCandidates.find((candidate) => Array.isArray(candidate));
      if (nestedList) return nestedList;
    }
    return null;
  };

  const getUsernameFromToken = () => {
    const token = storage.get("token");
    if (!token) return "";
    const parts = String(token).split(".");
    if (parts.length < 2) return "";
    try {
      const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, "=");
      const payload = JSON.parse(atob(padded));
      return String(payload?.sub || "");
    } catch (error) {
      return "";
    }
  };

  const currentUser = getUsernameFromToken();
  const isAdminUser = currentUser.trim().toLowerCase() === "sebasariz@iammagis.com";

  const syncProfile = async () => {
    const profile = await getCurrentUser();
    if (!profile?.success) return null;
    const user = profile?.data?.user || {};
    const profileStatus = profile?.data?.profile || {};
    setCurrentProfile(user);
    if (typeof profile?.data?.activation_wallet_trc20 === "string" && profile.data.activation_wallet_trc20.trim()) {
      setActivationWallet(profile.data.activation_wallet_trc20.trim());
    }
    if (typeof profile?.data?.activation_pionex_recipient === "string" && profile.data.activation_pionex_recipient.trim()) {
      setActivationPionexRecipient(profile.data.activation_pionex_recipient.trim());
    }
    if (typeof profile?.data?.activation_pionex_uid === "string" && profile.data.activation_pionex_uid.trim()) {
      setActivationPionexUid(profile.data.activation_pionex_uid.trim());
    }
    if (profile?.data?.subscription && typeof profile.data.subscription === "object") {
      setSubscriptionInfo(profile.data.subscription);
    }
    const symbol = getPrimaryTradingSymbol(user);
    const configuredSymbols = getConfiguredPairs(user).map((pair) => pair.symbol);
    if (symbol && (!selectedSymbolRef.current || !configuredSymbols.includes(selectedSymbolRef.current))) {
      setSelectedSymbol(symbol);
    }
    const mode = String(user?.trade_mode || "").trim().toUpperCase();
    if (mode) {
      setTradeMode(mode);
    }
    const view = normalizeDashboardView(user?.dashboard_view || user?.dashboardView);
    setDashboardView(view);
    const needsSetup = Boolean(profileStatus?.needs_setup);
    const shouldPrompt = Boolean(user?.preregistration_source) && !Boolean(profileStatus?.prompt_hidden);
    setProfileSetupLocked(needsSetup);
    if (shouldPrompt) {
      setSelfProfileState({ loading: false, error: "", success: "" });
      setShowProfileModal(true);
    }
    return user;
  };

  const loadAdminUsers = async () => {
    setAdminUsersLoading(true);
    setAdminUsersError("");
    const result = await getAdminUsers();
    if (!result.success) {
      setAdminUsersError(result.error || "No se pudo cargar usuarios");
    } else {
      setAdminUsers(result.data?.users || []);
    }
    setAdminUsersLoading(false);
  };

  useEffect(() => {
    logModalUserRef.current = logModalUser;
  }, [logModalUser]);

  const loadAdminOverview = async () => {
    setAdminOverviewLoading(true);
    setAdminOverviewError("");
    const result = await getAdminUsersOverview();
    if (!result.success) {
      setAdminOverviewError(result.error || "No se pudo cargar overview");
    } else {
      setAdminOverview(result.data?.users || []);
    }
    setAdminOverviewLoading(false);
  };

  const loadPreregistros = async () => {
    setPreregistrosLoading(true);
    setPreregistrosError("");
    const result = await getAdminPreregistros();
    if (!result.success) {
      setPreregistrosError(result.error || "No se pudo cargar preregistros");
    } else {
      setPreregistros(result.data?.items || []);
    }
    setPreregistrosLoading(false);
  };

  const fetchNewsState = useCallback(async () => {
    if (!NEWS_ENABLED) {
      return;
    }
    setNewsLoading(true);
    setNewsError("");
    const result = await getNewsState();
    if (result?.success) {
      setNewsState(normalizeNewsState(result.data));
    } else {
      setNewsError(result?.error || "No se pudo cargar noticias");
    }
    setNewsLoading(false);
  }, []);

  const fetchPairsStatus = useCallback(async () => {
    if (!showTradeView) return;
    setPairsStatusLoading(true);
    setPairsStatusError("");
    const result = await getTradingPairsStatus();
    if (result?.success) {
      setPairsStatus(Array.isArray(result.data?.pairs) ? result.data.pairs : []);
    } else {
      setPairsStatusError(result?.error || "No se pudo cargar estado de pares");
    }
    setPairsStatusLoading(false);
  }, [showTradeView]);

  // Motivo legible de un fallo de indicadores. Antes todo se resumia como "sin
  // indicadores disponibles todavia", que ocultaba si la causa era el exchange, un
  // simbolo no configurado o simplemente un timeout.
  const readIndicatorsFailureReason = (reason) => {
    const exchangeEstado = String(reason?.estadoMercado?.exchange?.estado || "").toUpperCase();
    if (exchangeEstado === "MANTENIMIENTO") return "el exchange tiene el par en mantenimiento";
    if (exchangeEstado === "SIN_DATOS") return "el exchange no entrega datos del símbolo";
    const status = reason?.response?.status;
    if (status) return `el backend respondió ${status}`;
    const message = String(reason?.message || "").trim();
    if (/timeout|timed out|exceeded/i.test(message)) return "la consulta tardó demasiado (timeout)";
    return message || "sin detalle";
  };

  const fetchPairIndicators = useCallback(async (pairs = []) => {
    const uniquePairs = [...new Map(
      pairs
        .filter((pair) => pair?.symbol)
        .map((pair) => [normalizeSymbol(pair.symbol), pair])
    ).values()];
    if (!showTradeView || !uniquePairs.length) {
      setPairIndicators({});
      setPairIndicatorsMissing([]);
      return;
    }
    setPairIndicatorsLoading(true);
    setPairIndicatorsError("");
    setPairIndicatorsMissing([]);
    const next = {};
    const missing = [];
    const forbidden = [];
    let failed = 0;

    try {
      for (let index = 0; index < uniquePairs.length; index += PAIR_INDICATORS_BATCH_SIZE) {
        const batch = uniquePairs.slice(index, index + PAIR_INDICATORS_BATCH_SIZE);
        const results = await Promise.allSettled(
          batch.map(async (pair) => {
            const symbol = normalizeSymbol(pair.symbol);
            const data = await getIndicators(symbol);
            return [symbol, data];
          })
        );
        let batchFailed = 0;
        results.forEach((result, batchIndex) => {
          if (result.status === "fulfilled") {
            const [symbol, data] = result.value;
            next[symbol] = data;
          } else {
            batchFailed += 1;
            const failedSymbol = normalizeSymbol(batch[batchIndex]?.symbol);
            if (result.reason?.isForbiddenIndicator) {
              forbidden.push(failedSymbol);
            } else {
              missing.push({ symbol: failedSymbol, motivo: readIndicatorsFailureReason(result.reason) });
            }
          }
        });
        failed += batchFailed;
        setPairIndicators((previous) => ({ ...previous, ...next }));
      }
      setPairIndicators(next);
      setPairIndicatorsMissing([...missing.map((item) => item.symbol), ...forbidden].filter(Boolean));
      if (failed) {
        const parts = [];
        if (missing.length) {
          const detail = missing
            .map((item) => `${item.symbol}: ${item.motivo}`)
            .join("; ");
          parts.push(`${missing.length} par(es) sin indicadores — ${detail}`);
        }
        if (forbidden.length) parts.push(`${forbidden.length} par(es) no configurados para esta cuenta`);
        setPairIndicatorsError(parts.join(". ") + ".");
      }
    } catch (error) {
      setPairIndicatorsError("No se pudo completar la lectura de indicadores.");
    } finally {
      setPairIndicatorsLoading(false);
    }
  }, [showTradeView]);

  // Al volver a primer plano: vaciar logs acumulados y refrescar pares/indicadores.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) return;
      flushLogBuffer();
      void fetchPairsStatus();
      if (pairMonitorRowsRef.current.length) {
        void fetchPairIndicators(pairMonitorRowsRef.current);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [flushLogBuffer, fetchPairsStatus, fetchPairIndicators]);

  useEffect(() => {
    if (!NEWS_ENABLED) return;
    const normalized = normalizeSymbol(selectedSymbol);
    if (!normalized) return;
    void fetchNewsState();
  }, [selectedSymbol, fetchNewsState, NEWS_ENABLED]);

  useEffect(() => {
    const normalized = normalizeSymbol(selectedSymbol);
    if (!normalized || !showTradeView || !showLiveBlocks) {
      setSymbolHistoryLogs([]);
      return;
    }

    let cancelled = false;
    setSymbolHistoryLogs([]);
    getSymbolLiveLog(normalized).then((result) => {
      if (cancelled) return;
      const lines = Array.isArray(result?.data?.lines) ? result.data.lines : [];
      setSymbolHistoryLogs(
        lines
          .filter((entry) => entry?.message)
          .map((entry) => ({
            message: String(entry.message || ""),
            level: String(entry.level || "INFO"),
            timestamp: entry.timestamp || new Date().toISOString(),
          }))
          .slice(-MAX_LIVE_BLOCK_LINES)
      );
    });

    return () => {
      cancelled = true;
    };
  }, [selectedSymbol, showTradeView, showLiveBlocks]);

  // ✅ CARGAR DATOS INICIALES Y WEBSOCKET - UNA SOLA VEZ
  useEffect(() => {
    // ✅ PROTECCIÓN: Solo ejecutar UNA VEZ
    if (wsInitializedRef.current) {
      console.log("⚠️ Dashboard ya inicializado, evitando duplicado");
      return;
    }
    wsInitializedRef.current = true;

    console.log("🚀 Inicializando Dashboard...");

    // ✅ SUSCRIBIRSE A EVENTOS - UNA SOLA VEZ
    const unsubscribe = wsManager.subscribe((data) => {
      if (!data || typeof data !== "object") return;
      if ("isTrusted" in data && !data.type) {
        console.warn("⚠️ Evento DOM recibido en listener WS, ignorando payload inválido");
        return;
      }

      const eventUser = String(data.user || data.account_id || "").toLowerCase();
      const isOrderEvent = [
        "order_update",
        "actualizacion_orden",
        "compra",
        "venta",
        "buy_event",
        "sell_event",
      ].includes(String(data.type || ""));
      if (isOrderEvent && !isAdminUser && eventUser && eventUser !== currentUser.toLowerCase()) {
        return;
      }

      if (debugWs) {
        console.log("📨 Evento recibido:", data.type);
      }

      if (data.type === "connection") {
        const rawStatus = String(data.status || "disconnected").toUpperCase();
        const status = rawStatus === "CONNECTED" ? "CONNECTED" : "DISCONNECTED";
        console.log(`🔗 Estado WebSocket: ${status}`);
        setWsStatus(status);
      }

      if (data.type === "notification") {
        if (debugWs) {
          console.log("🔔 Nueva notificación");
        }
        setNotifications((prev) => [data.notification, ...prev].slice(0, 10));
      }

      if (data.type === "log") {
        const logUser = String(data.user || data.account_id || "").toLowerCase();
        if (!logUser || logUser !== currentUser.toLowerCase()) {
          return;
        }
        logBufferRef.current.push({
          message: String(data.message || ""),
          level: String(data.level || "INFO"),
          timestamp: data.timestamp || new Date().toISOString(),
        });
        if (logBufferRef.current.length > MAX_LOG_BUFFER) {
          logBufferRef.current = logBufferRef.current.slice(-MAX_LOG_BUFFER);
        }
        scheduleLogFlush();
      }

      if (data.type === "user_log") {
        const modalUser = logModalUserRef.current;
        if (!modalUser) return;
        if (String(data.user || "").toLowerCase() !== modalUser.username.toLowerCase()) {
          return;
        }
        setLogModalState((prev) => ({
          loading: false,
          error: "",
          lines: [...prev.lines, String(data.message || "")].slice(-600),
        }));
      }

      if (data.type === "order_update") {
        if (debugWs) {
          console.log("📦 Orden actualizada");
        }
        const updatePayload = data?.data && typeof data.data === "object" ? data.data : data;
        if (activeOrdersUser === "ALL" || accountMatchesFilter(updatePayload, activeOrdersUser)) {
          setActiveOrders((prev) => applyOrderUpdate(prev, updatePayload));
        }
        requestActiveOrdersRefresh();
      }

      if (data.type === "actualizacion_orden") {
        const updatePayload = data?.data && typeof data.data === "object" ? data.data : data;
        if (activeOrdersUser === "ALL" || accountMatchesFilter(updatePayload, activeOrdersUser)) {
          setActiveOrders((prev) => applyOrderUpdate(prev, updatePayload));
        }
        requestActiveOrdersRefresh();
      }

      if (data.type === "compra" || data.type === "buy_event") {
        const nextOrder = buildOrderFromCompra(data);
        if (activeOrdersUser === "ALL" || accountMatchesFilter(nextOrder, activeOrdersUser)) {
          setActiveOrders((prev) => upsertOrder(prev, nextOrder));
        }
        bumpRefreshTrigger();
      }

      if (data.type === "venta" || data.type === "sell_event") {
        const orderId = getOrderIdValue(data);
        if (activeOrdersUser === "ALL" || accountMatchesFilter(data, activeOrdersUser)) {
          if (orderId) {
            setActiveOrders((prev) =>
              prev.filter((order) => getOrderIdValue(order) !== orderId)
            );
          }
          bumpRefreshTrigger();
        }
      }

      if (NEWS_ENABLED && data.type === "news_update") {
        const nextState = normalizeNewsState(data?.data || data);
        if (nextState) {
          const nextSymbol = normalizeSymbol(nextState?.symbol || data?.symbol || "");
          const allowedSymbols = getConfiguredPairs(currentProfile).map((pair) => pair.symbol);
          if (nextSymbol && allowedSymbols.length && !allowedSymbols.includes(nextSymbol)) {
            return;
          }
          setNewsState(nextState);
        }
      }

      if (
        data.type === "ordenes_abiertas" ||
        data.type === "active_orders" ||
        data.type === "orders_active"
      ) {
        const payloadOrders = extractOrdersArray(data);
        if (Array.isArray(payloadOrders)) {
          setActiveOrders((prev) =>
            areOrdersEquivalent(prev, payloadOrders) ? prev : payloadOrders
          );
        }
      }

      if (data.type === "compra" || data.type === "venta" || data.type === "buy_event" || data.type === "sell_event") {
        if (debugWs) {
          console.log(`💰 ${data.type} ejecutada`);
        }
        const isBuy = data.type === "compra" || data.type === "buy_event";
        const id = ++toastIdRef.current;
        const toast = {
          id,
          type: isBuy ? "compra" : "venta",
          symbol: data.symbol || "?",
          price: data.price || "?",
          size: data.size || "?",
          method: data.entry_method || data.method || "",
          timestamp: new Date(),
          entry_price: data.entry_price,
          sl: data.sl,
          tp: data.tp,
          sl_pct: data.sl_pct,
          tp_pct: data.tp_pct,
          hz_horas: data.hz_horas != null ? data.hz_horas : (data.sl == null && data.tp == null ? null : undefined),
          hz_vencimiento: data.hz_vencimiento != null ? toNumber(data.hz_vencimiento) : null,
          exit_type: isBuy ? null : (data.exit_type || data.razon_venta || data.reason || ""),
          pnl: isBuy ? null : data.pnl,
          pnl_percentage: isBuy ? null : data.pnl_percentage,
          razon_venta: isBuy ? null : (data.razon_venta || data.reason || ""),
        };
        setToasts((prev) => [...prev, toast]);
        setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, 8000);
        requestActiveOrdersRefresh();
        bumpRefreshTrigger();
      }
    });

    // ✅ CARGAR DATOS INICIALES
    const loadInitialData = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log("📥 Cargando datos iniciales...");
        const profileUser = await syncProfile();
        const initialDashboardView = normalizeDashboardView(
          profileUser?.dashboard_view || profileUser?.dashboardView || dashboardView
        );
        const initialShowTradeView = true;
        const tasks = [];
        if (initialShowTradeView) {
          tasks.push(fetchBalances());
          tasks.push(fetchActiveOrders());
          tasks.push(fetchPairsStatus());
        }
        if (NEWS_ENABLED && initialShowTradeView) {
          tasks.push(fetchNewsState());
        }
        if (isAdminUser) {
          tasks.push(loadAdminOverview());
          tasks.push(loadPreregistros());
          tasks.push(loadAdminUsers());
        }
        await Promise.all(tasks);
        console.log("✅ Datos iniciales cargados");
      } catch (err) {
        console.error("❌ Error cargando datos:", err);
        setError("Error al cargar datos del servidor");
      } finally {
        setLoading(false);
      }
    };

    // ✅ Estado inicial WS (sin asumir éxito del handshake)
    const initialWsStatus = String(wsManager.getStatus?.() || "DISCONNECTED").toUpperCase();
    setWsStatus(initialWsStatus === "CONNECTED" ? "CONNECTED" : "DISCONNECTED");
    if (initialWsStatus === "DISCONNECTED") {
      console.log("🔌 Solicitando conexión WebSocket...");
      wsManager.connect?.();
    }

    // ✅ EJECUTAR carga inicial
    loadInitialData();

    // ✅ CLEANUP: Desuscribirse al desmontar
    return () => {
      console.log("🧹 Limpiando Dashboard...");
      unsubscribe?.();
    };
  }, []); // ✅ ARRAY VACÍO: Solo ejecutar UNA VEZ al montar

  const fetchActiveOrders = async (silent = false, overrideUser) => {
    if (ordersFetchInFlightRef.current) return;
    ordersFetchInFlightRef.current = true;

    try {
      if (isAdminUser) {
        const userForFetch = overrideUser !== undefined ? overrideUser : activeOrdersUser;
        const targetUser = userForFetch === "ALL" ? "" : (userForFetch || "");
        const result = await getAdminActiveOrders(
          targetUser ? { user: targetUser } : {}
        );
        if (result?.success && Array.isArray(result.data)) {
          setActiveOrders((prev) =>
            areOrdersEquivalent(prev, result.data) ? prev : result.data
          );
          if (!silent) {
            console.log("✅ Órdenes admin cargadas:", result.data.length);
          }
        } else if (!silent) {
          console.error("❌ Error obteniendo órdenes admin");
          setError(result?.error || "Error al cargar órdenes admin");
        }
      } else {
        const data = await getActiveOrders();
        if (data && Array.isArray(data)) {
          setActiveOrders((prev) => {
            return areOrdersEquivalent(prev, data) ? prev : data;
          });
          if (!silent) {
            console.log("✅ Órdenes cargadas:", data.length);
          }
        } else {
          if (!silent) {
            console.error("❌ Error obteniendo órdenes");
            setError("Error al cargar órdenes");
          }
        }
      }
    } catch (error) {
      if (!silent) {
        console.error("❌ Excepción en órdenes:", error);
        setError(error.message);
      }
    } finally {
      ordersFetchInFlightRef.current = false;
    }
  };

  const handleActiveOrdersUserChange = (username) => {
    const next = username || "";
    setActiveOrdersUser(next);
    void fetchActiveOrders(true, next);
  };

  const handleManualSell = async (order) => {
    const orderId = getOrderIdValue(order);
    if (!orderId) return;
    try {
      const result = await sellActiveOrder(orderId);
      if (!result?.success) {
        setError(result?.error || "No se pudo vender la orden");
        return;
      }
      setActiveOrders((prev) =>
        prev.filter((item) => getOrderIdValue(item) !== orderId)
      );
      requestActiveOrdersRefresh();
      await fetchActiveOrders(true);
    } catch (err) {
      console.error("❌ Error vendiendo orden:", err);
      setError(err?.message || "Error vendiendo orden");
    }
  };

  const fetchBalances = async () => {
    try {
      const data = await getBalances();
      if (data && data.balances && Array.isArray(data.balances)) {
        setBalances(data.balances);
        setBalancesTotalUsdt(
          Number.isFinite(Number(data.total_usdt)) ? Number(data.total_usdt) : null
        );
        setBalancesWallet({
          spotUsdt: Number.isFinite(Number(data.spot_usdt)) ? Number(data.spot_usdt) : null,
          futuresUsdt: Number.isFinite(Number(data.futures_usdt)) ? Number(data.futures_usdt) : null,
        });
      } else {
        console.error("❌ Error estructura datos");
        setError("Estructura de datos inválida");
      }
    } catch (error) {
      console.error("❌ Excepción en balances:", error);
      setError(error.message);
    }
  };

  // Refresco de respaldo: si el WS está conectado no seguimos golpeando la API.
  useEffect(() => {
    if (!showTradeView) return undefined;
    const interval = setInterval(() => {
      if (document.hidden) return;
      if (wsStatus === "CONNECTED") return;
      void fetchActiveOrders(true);
    }, ACTIVE_ORDERS_POLL_MS);

    return () => clearInterval(interval);
  }, [showTradeView, wsStatus]);

  // ✅ Auto-refresh noticias cada 10 minutos
  useEffect(() => {
    if (!NEWS_ENABLED || !showTradeView) return undefined;
    const interval = setInterval(() => {
      if (document.hidden) return;
      void fetchNewsState();
    }, 600000);
    return () => clearInterval(interval);
  }, [fetchNewsState, NEWS_ENABLED, showTradeView]);

  const handleRefresh = async () => {
    console.log("🔄 Refrescando datos...");
    if (loading || refreshing) return;
    setRefreshing(true);
    bumpRefreshTrigger(0);
    setManualRefreshTrigger((prev) => prev + 1);
    try {
      const tasks = [syncProfile()];
      const profileUser = await tasks[0];
      const refreshDashboardView = normalizeDashboardView(
        profileUser?.dashboard_view || profileUser?.dashboardView || dashboardView
      );
      const refreshShowTradeView = true;
      tasks.length = 0;
      if (refreshShowTradeView) {
        tasks.push(fetchBalances());
        tasks.push(fetchPairsStatus());
      }
      if (NEWS_ENABLED && refreshShowTradeView) {
        tasks.push(fetchNewsState());
      }
      if (refreshShowTradeView) {
        tasks.push(fetchActiveOrders());
      }
      if (isAdminUser) {
        tasks.push(loadAdminOverview());
        tasks.push(loadPreregistros());
      }
      await Promise.all(tasks);
    } catch (error) {
      console.error("❌ Error en refresh manual:", error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleCloseError = () => setError(null);

  const handleCreateUser = async ({
    email,
    password,
    symbol,
    tradingPairs,
    pionexApiKey,
    pionexApiSecret,
    tradeMode,
    telegramChatIds,
    dashboardView,
  }) => {
    setAddUserState({ loading: true, error: "", success: "" });
    const result = await createAdminUser(email, password, {
      symbol,
      tradingPairs,
      tradeMode,
      pionexApiKey,
      pionexApiSecret,
      telegramChatIds,
      dashboardView,
    });
    if (!result.success) {
      setAddUserState({
        loading: false,
        error: result.error || "No se pudo crear el usuario",
        success: "",
      });
      return;
    }

    const emailSent = result.data?.email_sent;
    const successMessage = emailSent
      ? "Usuario creado y correo enviado."
      : "Usuario creado. No se pudo enviar el correo.";
    setAddUserState({
      loading: false,
      error: "",
      success: successMessage,
    });
    await loadAdminUsers();
    if (isAdminUser) {
      await loadAdminOverview();
      await loadPreregistros();
    }
  };

  const handleResendUser = async (email) => {
    if (!email) return;
    setAddUserState({ loading: false, error: "", success: "" });
    setAdminUsersBusy((prev) => ({ ...prev, resend: email }));
    const result = await resendAdminUserAccess(email);
    setAdminUsersBusy((prev) => ({ ...prev, resend: "" }));
    if (!result.success) {
      setAddUserState({
        loading: false,
        error: result.error || "No se pudo reenviar accesos",
        success: "",
      });
      return;
    }
    const emailSent = result.data?.email_sent;
    setAddUserState({
      loading: false,
      error: "",
      success: emailSent
        ? "Accesos reenviados (nueva clave enviada)."
        : "Accesos actualizados, pero no se pudo enviar el correo.",
    });
  };

  const handleDeleteUser = async (email) => {
    if (!email) return;
    const confirmed = window.confirm(`Eliminar usuario ${email}?`);
    if (!confirmed) return;
    setAddUserState({ loading: false, error: "", success: "" });
    setAdminUsersBusy((prev) => ({ ...prev, delete: email }));
    const result = await deleteAdminUser(email);
    setAdminUsersBusy((prev) => ({ ...prev, delete: "" }));
    if (!result.success) {
      setAddUserState({
        loading: false,
        error: result.error || "No se pudo eliminar el usuario",
        success: "",
      });
      return;
    }
    setAddUserState({
      loading: false,
      error: "",
      success: "Usuario eliminado.",
    });
    setAdminUsers((prev) => prev.filter((user) => user?.username !== email));
    if (isAdminUser) {
      await loadAdminOverview();
      await loadPreregistros();
    }
  };

  const handleOpenConsole = async (user) => {
    setLogModalUser(user);
    setLogModalState({ loading: true, error: "", lines: [] });
    wsManager.send({
      type: "subscribe_user_log",
      email: user.username,
      lines: 200,
    });
  };

  const handleRefreshConsole = async () => {
    if (!logModalUser) return;
    setLogModalState((prev) => ({ ...prev, loading: true, lines: [] }));
    wsManager.send({
      type: "subscribe_user_log",
      email: logModalUser.username,
      lines: 200,
    });
  };

  const handleCloseConsole = () => {
    if (logModalUserRef.current?.username) {
      wsManager.send({
        type: "unsubscribe_user_log",
        email: logModalUserRef.current.username,
      });
    }
    setLogModalUser(null);
  };

  const handleRestartBot = async (user) => {
    if (!user?.username) return;
    const confirmed = window.confirm(`Reiniciar bot de ${user.username}?`);
    if (!confirmed) return;
    const result = await restartAdminUserBot(user.username);
    if (!result.success) {
      alert(result.error || "No se pudo reiniciar el bot");
      return;
    }
    await loadAdminOverview();
    await loadPreregistros();
  };

  const handleStartBot = async (user) => {
    if (!user?.username) return;
    const confirmed = window.confirm(`Activar bot de ${user.username}?`);
    if (!confirmed) return;
    const result = await startAdminUserBot(user.username);
    if (!result.success) {
      alert(result.error || "No se pudo activar el bot");
      return;
    }
    await loadAdminOverview();
    await loadPreregistros();
  };

  const handleStopBot = async (user) => {
    if (!user?.username) return;
    const confirmed = window.confirm(`Inactivar bot de ${user.username}?`);
    if (!confirmed) return;
    const result = await stopAdminUserBot(user.username);
    if (!result.success) {
      alert(result.error || "No se pudo inactivar el bot");
      return;
    }
    await loadAdminOverview();
  };

  const handleUpdateUser = async ({
    email,
    fullName,
    mobilePhone,
    symbol,
    tradingPairs,
    pionexApiKey,
    pionexApiSecret,
    tradeMode,
    telegramChatIds,
    dashboardView,
    subscriptionPct,
    subscriptionPeriodDays,
    subscriptionPeriodType,
  }) => {
    if (!email) return;
    setEditUserState({ loading: true, error: "", success: "" });
    const payload = {
      full_name: fullName,
      mobile_phone: mobilePhone,
      symbol,
      trading_pairs: tradingPairs,
      trade_mode: tradeMode,
      pionex_api_key: pionexApiKey,
      pionex_api_secret: pionexApiSecret,
      telegram_chat_ids: telegramChatIds,
      dashboard_view: dashboardView,
      subscription_pct: subscriptionPct,
      subscription_period_days: subscriptionPeriodDays,
      subscription_period_type: subscriptionPeriodType,
    };
    const result = await updateAdminUser(email, payload);
    if (!result.success) {
      setEditUserState({
        loading: false,
        error: result.error || "No se pudo actualizar el usuario",
        success: "",
      });
      return;
    }
    setEditUserState({
      loading: false,
      error: "",
      success: "Usuario actualizado.",
    });
    await loadAdminUsers();
    await loadAdminOverview();
    await loadPreregistros();
  };

  const handleSubscriptionConfirmed = async () => {
    await loadAdminUsers();
    await loadAdminOverview();
    await loadPreregistros();
  };

  const saveAdminTradingPairs = async (user, nextPairs, actionLabel) => {
    if (!user?.username) return false;
    const normalizedPairs = Array.isArray(nextPairs) && nextPairs.length
      ? normalizeTradingPairs(nextPairs, user?.symbol || "")
      : [];
    const payload = {
      symbol: normalizedPairs[0]?.symbol || "",
      trading_pairs: normalizedPairs,
    };
    const result = await updateAdminUser(user.username, payload);
    if (!result.success) {
      window.alert(result.error || `No se pudo ${actionLabel}.`);
      return false;
    }
    await loadAdminUsers();
    await loadAdminOverview();
    return true;
  };

  const handleApplyPairReplacement = async (user, replacement) => {
    const currentPairs = Array.isArray(user?.trading_pairs) ? user.trading_pairs : [];
    const nextPairs = currentPairs
      .filter((pair) => {
        const symbol = String(pair?.symbol || "").toUpperCase();
        const marketType = String(pair?.market_type || "").toUpperCase();
        return !(
          symbol === String(replacement?.remove_symbol || "").toUpperCase() &&
          marketType === String(replacement?.remove_market_type || "").toUpperCase()
        );
      })
      .concat([
        {
          symbol: replacement?.add_symbol,
          market_type: replacement?.add_market_type,
        },
      ]);
    return await saveAdminTradingPairs(user, nextPairs, "aplicar la sustitución");
  };

  const handleApplyPairAddition = async (user, candidate) => {
    const currentPairs = Array.isArray(user?.trading_pairs) ? user.trading_pairs : [];
    const nextPairs = currentPairs.concat([
      {
        symbol: candidate?.symbol,
        market_type: candidate?.market_type,
      },
    ]);
    return await saveAdminTradingPairs(user, nextPairs, "agregar el par");
  };

  const handleApplyAllPairSuggestions = async (user) => {
    if (!user?.username) return false;
    const result = await applyAdminUserPairSuggestions(user.username);
    if (!result.success) {
      window.alert(result.error || "No se pudieron aplicar las sugerencias.");
      return false;
    }
    await loadAdminUsers();
    await loadAdminOverview();
    return true;
  };

  const handleRemoveTradingPair = async (user, pair, totalPairs = 0) => {
    const symbol = String(pair?.symbol || "").toUpperCase();
    const marketType = String(pair?.market_type || "").toUpperCase();
    if (!symbol) return false;
    const confirmMessage =
      totalPairs <= 1
        ? `Vas a quitar ${symbol} y el usuario quedará sin pares configurados. ¿Continuamos?`
        : `Quitar ${symbol} de la lista de pares configurados?`;
    if (!window.confirm(confirmMessage)) return false;

    const currentPairs = Array.isArray(user?.trading_pairs) ? user.trading_pairs : [];
    const nextPairs = currentPairs.filter((item) => {
      const itemSymbol = String(item?.symbol || "").toUpperCase();
      const itemMarketType = String(item?.market_type || "").toUpperCase();
      return !(itemSymbol === symbol && itemMarketType === marketType);
    });
    return await saveAdminTradingPairs(user, nextPairs, "eliminar el par");
  };

  const handleUpdateMyProfile = async ({
    fullName,
    mobilePhone,
    symbol,
    tradingPairs,
    pionexApiKey,
    pionexApiSecret,
    telegramChatIds,
    hideProfilePrompt,
    newPassword,
    confirmPassword,
  }) => {
    if (newPassword && newPassword !== confirmPassword) {
      setSelfProfileState({
        loading: false,
        error: "La confirmación de la nueva contraseña no coincide",
        success: "",
      });
      return;
    }
    setSelfProfileState({ loading: true, error: "", success: "" });
    const payload = {
      full_name: fullName,
      mobile_phone: mobilePhone,
      symbol,
      trading_pairs: tradingPairs,
      pionex_api_key: pionexApiKey,
      pionex_api_secret: pionexApiSecret,
      telegram_chat_ids: telegramChatIds,
      hide_profile_prompt: hideProfilePrompt,
    };
    const result = await updateCurrentUserProfile(payload);
    if (!result.success) {
      setSelfProfileState({
        loading: false,
        error: result.error || "No se pudo actualizar el perfil",
        success: "",
      });
      return;
    }
    if (newPassword) {
      const passwordResult = await updateCurrentUserPassword(newPassword);
      if (!passwordResult.success) {
        setSelfProfileState({
          loading: false,
          error: passwordResult.error || "No se pudo cambiar la contraseña",
          success: "",
        });
        return;
      }
    }
    setSelfProfileState({
      loading: false,
      error: "",
      success: newPassword ? "Perfil y contraseña actualizados." : "Perfil actualizado.",
    });
    const updatedUser = await syncProfile();
    if (updatedUser) {
      setShowProfileModal(false);
      setProfileSetupLocked(false);
    }
  };

  const handleApprovePrereg = async (payload) => {
    setApprovePreregState({ loading: true, error: "", success: "" });
    setPreregistroBusyEmail(payload?.email || "");
    const result = await approvePreregistro(payload);
    setPreregistroBusyEmail("");
    if (!result.success) {
      setApprovePreregState({
        loading: false,
        error: result.error || "No se pudo aprobar el preregistro",
        success: "",
      });
      return;
    }
    setApprovePreregState({
      loading: false,
      error: "",
      success: "Preregistro aprobado.",
    });
    await loadPreregistros();
    await loadAdminUsers();
    await loadAdminOverview();
    setApprovePreregItem(null);
  };

  const handleDeletePrereg = async (email) => {
    if (!email) return;
    const confirmed = window.confirm(`Eliminar preregistro ${email}?`);
    if (!confirmed) return;
    setPreregistroBusyEmail(email);
    const result = await deletePreregistro(email);
    setPreregistroBusyEmail("");
    if (!result.success) {
      setPreregistrosError(result.error || "No se pudo eliminar el preregistro");
      return;
    }
    await loadPreregistros();
  };

  // ✅ LOGOUT MEJORADO
  const handleLogout = async () => {
    try {
      console.log("🚪 Iniciando logout...");

      // 1️⃣ DESCONECTAR WEBSOCKET
      if (wsManager) {
        wsManager.disconnect?.();
        console.log("✅ WebSocket desconectado");
      }

      // 2️⃣ LLAMAR AL LOGOUT DEL BACKEND
      try {
        console.log("📤 Enviando logout al servidor...");
        await logoutUser();
        console.log("✅ Logout en backend completado");
      } catch (err) {
        console.warn("⚠️ Error en logout backend (no crítico):", err);
      }

      // 3️⃣ LIMPIAR STATE
      console.log("🧹 Limpiando state local...");
      setActiveOrders([]);
      setBalances([]);
      setBalancesTotalUsdt(null);
      setNotifications([]);
      setLogLines([]);
      setLoading(false);

      // 4️⃣ LIMPIAR ALMACENAMIENTO
      console.log("🗑️ Limpiando almacenamiento local...");
      try {
        storage.clear();
      } catch (error) {
        console.warn("⚠️ No se pudo limpiar storage:", error);
      }
      try {
        sessionStorage.clear();
      } catch (error) {
        console.warn("⚠️ No se pudo limpiar sessionStorage:", error);
      }
      console.log("✅ Almacenamiento limpiado");

      // 5️⃣ PEQUEÑA ESPERA
      await new Promise(resolve => setTimeout(resolve, 500));

      // 6️⃣ REDIRIGIR
      console.log("🔄 Redirigiendo a /login...");
      window.location.href = "/login";

    } catch (error) {
      console.error("❌ Error en logout:", error);
      window.location.href = "/login";
    }
  };

  const liveBlocksLogs = useMemo(() => {
    const historical = Array.isArray(symbolHistoryLogs) ? symbolHistoryLogs : [];
    const live = Array.isArray(liveBlocksLines) ? liveBlocksLines : [];
    // Mantener el ciclo histórico del símbolo al final para que el recorte no lo expulse
    // cuando el WebSocket trae muchas líneas recientes de otros pares.
    return [...live, ...historical].slice(-MAX_LIVE_BLOCK_LINES);
  }, [symbolHistoryLogs, liveBlocksLines]);

  const estadoMercadoGlobal = useMemo(() => {
    // La jornada es la misma para todos los pares, asi que se lee UNA vez desde
    // cualquiera que lo traiga y se muestra en el encabezado. Sin esto cada tarjeta
    // repetia "Fuera de jornada NY (modo reducido)".
    const conEstado = (Array.isArray(pairsStatus) ? pairsStatus : []).find(
      (item) => item?.estado_mercado
    );
    return conEstado?.estado_mercado || null;
  }, [pairsStatus]);

  const pairMonitorRows = useMemo(() => {
    const statusPairs = (Array.isArray(pairsStatus) ? pairsStatus : [])
      .map((item) => {
        const symbol = normalizeSymbol(item?.symbol);
        if (!symbol) return null;
        const market = String(item?.market_type || item?.market || "")
          .trim()
          .toUpperCase();
        return {
          symbol,
          market_type: market || (symbol.endsWith("_PERP") ? "FUTURES" : "SPOT"),
        };
      })
      .filter(Boolean);
    const configuredFromProfile = getConfiguredPairs(currentProfile);
    const sourcePairs = configuredFromProfile.length ? configuredFromProfile : statusPairs;
    const statusMap = new Map(
      (Array.isArray(pairsStatus) ? pairsStatus : []).map((item) => [normalizeSymbol(item?.symbol), item])
    );
    return sourcePairs.map((pair) => {
      const status = statusMap.get(pair.symbol) || {};
      const openOrdersForPair = activeOrders.filter((order) => normalizeSymbol(order?.symbol) === pair.symbol).length;
      return {
        ...pair,
        ...status,
        open_orders: Number(status.open_orders ?? openOrdersForPair ?? 0),
      };
    });
  }, [currentProfile, pairsStatus, activeOrders]);

  // Mantener el ref al día con el último pairMonitorRows calculado.
  pairMonitorRowsRef.current = pairMonitorRows;

  const liveActiveOrders = useMemo(() => {
    if (!Array.isArray(activeOrders) || !activeOrders.length) return [];
    return activeOrders.map((order) => {
      const symbol = normalizeSymbol(order?.symbol);
      const indicatorPayload = pairIndicators?.[symbol];
      const indicator =
        indicatorPayload && typeof indicatorPayload === "object"
          ? (indicatorPayload.data && typeof indicatorPayload.data === "object"
              ? indicatorPayload.data
              : indicatorPayload)
          : null;
      const livePrice = toNumber(
        indicator?.close ?? indicator?.precio_actual ?? indicator?.price
      );
      const buyPrice = toNumber(order?.precio_compra ?? order?.buy_price ?? order?.price);
      const qty = toNumber(order?.cantidad ?? order?.size);
      const currentPrice = livePrice ?? toNumber(order?.precio_actual ?? order?.price);

      if (currentPrice === null) {
        return order;
      }

      const pnlPrice =
        buyPrice !== null && qty !== null
          ? (currentPrice - buyPrice) * qty
          : toNumber(order?.p_l_precio);
      const pnlPct =
        buyPrice !== null && buyPrice > 0
          ? ((currentPrice - buyPrice) / buyPrice) * 100
          : toNumber(order?.p_l_porcentaje);
      const maxPrice = Math.max(
        toNumber(order?.max_precio) ?? Number.NEGATIVE_INFINITY,
        currentPrice
      );

      return {
        ...order,
        precio_actual: currentPrice,
        max_precio: Number.isFinite(maxPrice) ? maxPrice : order?.max_precio,
        p_l_precio: pnlPrice ?? order?.p_l_precio,
        p_l_porcentaje: pnlPct ?? order?.p_l_porcentaje,
        p_l_color:
          pnlPct === null
            ? order?.p_l_color
            : pnlPct >= 0
            ? "verde"
            : "rojo",
      };
    });
  }, [activeOrders, pairIndicators]);

  useEffect(() => {
    if (!pairMonitorRows.length) return;
    const available = pairMonitorRows.map((pair) => pair.symbol);
    if (!available.includes(normalizeSymbol(selectedSymbol))) {
      setSelectedSymbol(available[0]);
    }
  }, [pairMonitorRows, selectedSymbol]);

  const pairSymbolsKey = useMemo(
    () => pairMonitorRows.map((pair) => `${pair.market_type}:${pair.symbol}`).join("|"),
    [pairMonitorRows]
  );

  // Refresco pausado del panel de pares para evitar presión innecesaria.
  useEffect(() => {
    if (!showTradeView || loading) return undefined;
    const interval = setInterval(() => {
      if (document.hidden) return;
      void fetchPairsStatus();
      const current = pairMonitorRowsRef.current;
      if (current.length) {
        void fetchPairIndicators(current);
      }
    }, PAIRS_STATUS_POLL_MS);

    return () => clearInterval(interval);
  }, [showTradeView, loading, fetchPairsStatus, fetchPairIndicators, pairMonitorRowsRef]);

  useEffect(() => {
    if (!showTradeView || loading) return;
    void fetchPairIndicators(pairMonitorRows);
  }, [pairSymbolsKey, showTradeView, loading, refreshTrigger, manualRefreshTrigger, fetchPairIndicators]);

  return (
    <div className="dashboard-wrapper">
      {/* ✅ CONTENEDOR PRINCIPAL */}
      <div className="dashboard-container">
        {/* ✅ HEADER CON ESTADO Y CONTROLES */}
        <div className="dashboard-header">
          <div className="header-left">
            <h1>📊 Trade - Iam Magis</h1>
            <div className={`connection-status ${wsStatus.toLowerCase()}`}>
              <span className="status-dot"></span>
              <span className="status-text">{wsStatus}</span>
            </div>
          </div>

          <div className="header-right">
            {/* ✅ SÍMBOLO ACTIVO */}
            <div className="symbol-selector" title="Pares en operación">
              <span className="symbol-active-value">{getTradingPairsLabel(currentProfile)}</span>
              {tradeMode ? <span className="symbol-active-mode">{tradeMode}</span> : null}
            </div>

            {/* ✅ BOTÓN NOTIFICACIONES */}
            {showTradeView && (
              <>
                <button
                  className="btn-notifications"
                  onClick={() => setShowNotifications(!showNotifications)}
                  title={`${notifications.length} notificaciones`}
                >
                  🔔
                  {notifications.length > 0 && (
                    <span className="notification-badge">
                      {notifications.length > 99 ? "99+" : notifications.length}
                    </span>
                  )}
                </button>

                <button
                  className="btn-console"
                  onClick={() => setShowConsole(!showConsole)}
                  title={`${logLines.length} líneas de log`}
                >
                  🖥️
                  {logLines.length > 0 && (
                    <span className="console-badge">
                      {logLines.length > 999 ? "999+" : logLines.length}
                    </span>
                  )}
                </button>

                <button
                  className={`btn-liveblocks ${showLiveBlocks ? "on" : "off"}`}
                  onClick={() => setShowLiveBlocks((prev) => !prev)}
                  title={showLiveBlocks ? "Ocultar bloques en vivo" : "Mostrar bloques en vivo"}
                >
                  🧱
                </button>

                <button
                  className={`btn-orders ${showOrders ? "on" : "off"}`}
                  onClick={() => setShowOrders((prev) => !prev)}
                  title={showOrders ? "Ocultar Órdenes Activas" : "Mostrar Órdenes Activas"}
                >
                  📋
                </button>

                <button
                  className={`btn-news ${showNews ? "on" : "off"}`}
                  onClick={() => setShowNews((prev) => !prev)}
                  title={showNews ? "Ocultar noticias" : "Mostrar noticias"}
                >
                  📰
                </button>
              </>
            )}

            {isAdminUser && (
              <button
                className="btn-add-user"
                onClick={() => {
                  setAddUserState({ loading: false, error: "", success: "" });
                  setShowAddUserModal(true);
                  void loadAdminUsers();
                }}
                title="Agregar usuario"
              >
                <span className="btn-icon">➕</span>
                <span className="btn-text">Usuario</span>
              </button>
            )}

            <button
              className="btn-profile"
              onClick={() => {
                setSelfProfileState({ loading: false, error: "", success: "" });
                setShowProfileModal(true);
              }}
              title="Actualizar mi perfil"
            >
              <span className="btn-icon">👤</span>
              <span className="btn-text">Perfil</span>
            </button>

            {/* ✅ BOTÓN ACTUALIZAR */}
            <button
              onClick={handleRefresh}
              disabled={loading || refreshing}
              className="btn-refresh"
              title="Refrescar datos"
            >
              <span className="btn-icon">🔄</span>
              <span className="btn-text">{loading || refreshing ? "..." : "Actualizar"}</span>
            </button>

            {/* ✅ BOTÓN LOGOUT */}
            <button
              onClick={handleLogout}
              className="btn-logout"
              title="Cerrar sesión"
            >
              <span className="btn-icon">🚪</span>
              <span className="btn-text">Logout</span>
            </button>
          </div>
        </div>

        {/* ✅ ERROR BANNER */}
        {error && (
          <div className="alert alert-error">
            <div className="alert-content">
              <span className="alert-icon">⚠️</span>
              <span className="alert-message">{error}</span>
            </div>
            <button
              onClick={handleCloseError}
              className="alert-close"
              title="Cerrar"
            >
              ✕
            </button>
          </div>
        )}

        {/* ✅ LOADING STATE */}
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Cargando datos del servidor...</p>
          </div>
        ) : (
          <>
            {/* ✅ FILA 1: NOTIFICACIONES (OCULTO/VISIBLE) */}
            {showTradeView && showNotifications && (
              <div className="dashboard-row full-width">
                <div className="notifications-wrapper"> 
                  <Notifications
                    notifications={notifications}
                    loading={loading || refreshing}
                  />
                </div>
              </div>
            )}

            {/* ✅ FILA 2: NOTICIAS EN ANÁLISIS */}
            {showTradeView && NEWS_ENABLED && showNews && (
              <div className="dashboard-row full-width">
                <div className="column">
                  <NewsCarousel
                    data={newsState}
                    loading={newsLoading}
                    error={newsError}
                    buildTag={NEWS_CAROUSEL_TAG}
                  />
                </div>
              </div>
            )}

            {showTradeView && pairMonitorRows.length > 0 && (
              <div className="dashboard-row full-width">
                <div className="pair-focus-selector">
                  <div>
                    <span>Par visible en tarjetas</span>
                    <strong>{selectedSymbol}</strong>
                  </div>
                  <div className="pair-focus-options">
                    {pairMonitorRows.map((pair) => (
                      <button
                        key={`${pair.market_type}:${pair.symbol}`}
                        type="button"
                        className={normalizeSymbol(selectedSymbol) === pair.symbol ? "active" : ""}
                        onClick={() => setSelectedSymbol(pair.symbol)}
                      >
                        <b>{pair.symbol}</b>
                        <small>{pair.market_type === "FUTURES" ? "Futuros" : "Spot"}</small>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {showTradeView && (
              <div className="dashboard-row full-width">
                <div className="pairs-monitor">
                  <div className="pairs-monitor-header">
                    <div>
                      <h3>Pares en análisis</h3>
                      <p>{pairMonitorRows.length} {pairMonitorRows.length === 1 ? "par configurado" : "pares configurados"} en esta cuenta</p>
                      {/* La jornada se informa UNA vez aqui y no en cada tarjeta: es una
                          propiedad de la hora, no del par, y repetirla 12 veces no dice
                          nada de ninguno. En las tarjetas solo van los pares que
                          realmente no se pueden negociar. */}
                      <JornadaResumen jornada={estadoMercadoGlobal?.jornada} />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        void fetchPairsStatus();
                        void fetchPairIndicators(pairMonitorRows);
                      }}
                      disabled={pairsStatusLoading || pairIndicatorsLoading}
                    >
                      {pairsStatusLoading || pairIndicatorsLoading ? "..." : "Actualizar"}
                    </button>
                  </div>
                  {pairsStatusError ? <div className="pairs-monitor-error">{pairsStatusError}</div> : null}
                  {pairIndicatorsError ? (
                    <div className="pairs-monitor-error">
                      <div>{pairIndicatorsError}</div>
                      {pairIndicatorsMissing.length ? (
                        <div className="pairs-monitor-missing-list">
                          {pairIndicatorsMissing.join(", ")}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="pairs-monitor-grid">
                    {pairMonitorRows.map((pair) => {
                      const indicators = pairIndicators[pair.symbol] || {};
                      const reading = Object.keys(indicators).length ? getIndicatorReading(indicators) : null;
                      const tone = reading?.tone || pair.buy_tone || "muted";
                      const scoreValue = reading?.score ?? pair.buy_score;
                      const score = scoreValue === null || scoreValue === undefined ? "N/A" : `${Math.round(scoreValue)}/100`;
                      const price = formatPrice(indicators.close ?? pair.price);
                      const trend = indicators.trend || pair.trend || "N/A";
                      const macd = toNumber(indicators.MACD);
                      const macdSignal = toNumber(indicators.MACD_signal);
                      const rsiValue = toNumber(indicators.RSI);
                      const macdState = macd === null || macdSignal === null ? "N/A" : macd >= macdSignal ? "Alcista" : "Bajista";
                      const volume = toNumber(indicators.volume);
                      const volumeMa = toNumber(indicators.volume_ma);
                      const volumeRatio = volume !== null && volumeMa ? `${(volume / volumeMa).toFixed(2)}x` : "N/A";
                      const bbUpper = toNumber(indicators.BB_upper_1h_5m ?? indicators.BB_upper);
                      const bbLower = toNumber(indicators.BB_lower_1h_5m ?? indicators.BB_lower);
                      const close = toNumber(indicators.close ?? pair.price);
                      const bbPosition = close !== null && bbUpper !== null && bbLower !== null && bbUpper > bbLower
                        ? `${Math.max(0, Math.min(100, ((close - bbLower) / (bbUpper - bbLower)) * 100)).toFixed(0)}%`
                        : "N/A";
                      return (
                        <div className={`pair-monitor-card ${tone}`} key={`${pair.market_type}:${pair.symbol}`}>
                          <div className="pair-monitor-top">
                            <strong>{pair.symbol}</strong>
                            <span>{pair.market_type === "FUTURES" ? "Futuros" : "Spot"}</span>
                          </div>
                          <div className="pair-monitor-state">
                            <b>{reading?.label || pair.buy_label || "Sin datos"}</b>
                            <span>{score}</span>
                          </div>
                          <MarketStateBadges estado={pair.estado_mercado} />
                          <div className="pair-monitor-metrics">
                            <div>
                              <small>Precio</small>
                              <b>{price}</b>
                              <em>{trend}</em>
                            </div>
                            <div>
                              <small>Órdenes</small>
                              <b>{pair.open_orders || 0}</b>
                              <em>{pair.open_orders ? "Abierta" : "Libre"}</em>
                            </div>
                            <div>
                              <small>RSI</small>
                              <b>{formatNumber(indicators.RSI, 1)}</b>
                              <em>{rsiValue === null ? "N/A" : rsiValue >= 70 ? "Alto" : rsiValue <= 35 ? "Bajo" : "Normal"}</em>
                            </div>
                            <div>
                              <small>MACD</small>
                              <b>{macdState}</b>
                              <em>{formatNumber(macd, 5)}</em>
                            </div>
                            <div>
                              <small>Volumen</small>
                              <b>{volumeRatio}</b>
                              <em>{formatNumber(volume, 0)}</em>
                            </div>
                            <div>
                              <small>Bandas</small>
                              <b>{bbPosition}</b>
                              <em>posición</em>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {!pairMonitorRows.length ? (
                      <div className="pairs-monitor-empty">No hay pares configurados para trading.</div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}

            {/* ✅ FILA 2: ÓRDENES ACTIVAS */}
            {showTradeView && showOrders && (
              <div className="dashboard-row full-width">
                <div className="column">
                  <OrdersTable
                    data={liveActiveOrders}
                    onRefresh={fetchActiveOrders}
                    loading={loading || refreshing}
                    title="📋 Órdenes Activas"
                    symbol={isAdminUser ? "TODOS" : selectedSymbol}
                    showUser={isAdminUser}
                    onSell={handleManualSell}
                    showUserSelector={isAdminUser}
                    currentUser={currentUser}
                    adminUsers={adminUsers}
                    selectedUser={activeOrdersUser}
                    onUserChange={handleActiveOrdersUserChange}
                  />
                </div>
              </div>
            )}

            {/* ✅ FILA 3: BLOQUES EN VIVO (WebSocket) */}
            {showTradeView && showLiveBlocks && (
              <div className="dashboard-row full-width">
                <div className="column">
                  <LiveBlocks logs={liveBlocksLogs} symbol={selectedSymbol} />
                </div>
              </div>
            )}

            {/* ✅ FILA 4: CONSOLA (debajo de bloques en vivo) */}
            {showTradeView && showConsole && (
              <div className="dashboard-row full-width">
                <div className="console-wrapper">
                  <LogConsole
                    logs={logLines}
                    onClear={handleClearLogs}
                  />
                </div>
              </div>
            )}

            {/* ✅ FILA 5: INDICADORES */}
            {showTradeView && (
              <div className="dashboard-row full-width">
                <div className="column">
                  <Indicators
                    symbol={selectedSymbol}
                    refreshTrigger={refreshTrigger}
                    manualRefreshTrigger={manualRefreshTrigger}
                    availablePairs={pairMonitorRows}
                    onSymbolChange={setSelectedSymbol}
                  />
                </div>
              </div>
            )}

            {/* ✅ FILA 6: BALANCES + GRÁFICO */}
            {showTradeView && (
              <div className="dashboard-row two-columns">
                <div className="column">
                  <Balances
                    data={balances}
                    totalUsdt={balancesTotalUsdt}
                    spotUsdt={balancesWallet.spotUsdt}
                    futuresUsdt={balancesWallet.futuresUsdt}
                    onRefresh={fetchBalances}
                    loading={loading || refreshing}
                  />
                </div>

                <div className="column">
                  <IndicatorsChart
                    symbol={selectedSymbol}
                    refreshTrigger={refreshTrigger}
                    manualRefreshTrigger={manualRefreshTrigger}
                    availablePairs={pairMonitorRows}
                    onSymbolChange={setSelectedSymbol}
                  />
                </div>
              </div>
            )}

            {isAdminUser && (
              <div className="dashboard-row full-width">
                <div className="column">
                  <MultiPairChart
                    availablePairs={pairMonitorRows}
                    currentUser={currentUser}
                  />
                </div>
              </div>
            )}

            {isAdminUser && (preregistrosLoading || preregistrosError || preregistros.length > 0) && (
              <div className="dashboard-row full-width">
                <PreregistrosOverview
                  items={preregistros}
                  loading={preregistrosLoading}
                  error={preregistrosError}
                  onRefresh={loadPreregistros}
                  onApprove={(item) => {
                    setApprovePreregState({ loading: false, error: "", success: "" });
                    setApprovePreregItem(item);
                  }}
                  onDelete={handleDeletePrereg}
                  busyEmail={preregistroBusyEmail}
                />
              </div>
            )}

            {isAdminUser && (
              <div className="dashboard-row full-width">
                <AdminUsersOverview
                  users={adminOverview}
                  loading={adminOverviewLoading}
                  error={adminOverviewError}
                  onRefresh={loadAdminOverview}
                  onEdit={(user) => {
                    setEditUserState({ loading: false, error: "", success: "" });
                    setEditUser(user);
                  }}
                  onConsole={handleOpenConsole}
                  onRestart={handleRestartBot}
                  onStart={handleStartBot}
                  onStop={handleStopBot}
                  onDelete={handleDeleteUser}
                  onMarkPaid={async (user) => {
                    const autoAmount =
                      Number(user?.sub?.payment_pending_amount) > 0
                        ? Number(user?.sub?.payment_pending_amount)
                        : Number(user?.subscription_pending_amount) > 0
                        ? Number(user?.subscription_pending_amount)
                        : Number(user?.sub?.due_amount) > 0
                        ? Number(user?.sub?.due_amount)
                        : Number(user?.subscription_due_amount) > 0
                        ? Number(user?.subscription_due_amount)
                        : null;
                    const result = await markSubscriptionPaid(user.username, {
                      amount: autoAmount,
                      reference: "admin_tabla",
                    });
                    if (result.success) {
                      loadAdminOverview();
                    } else {
                      window.alert(result.error || "Error marcando el pago como realizado.");
                    }
                  }}
                  busyDeleteEmail={adminUsersBusy.delete}
                  onApplyReplacement={handleApplyPairReplacement}
                  onApplyAddition={handleApplyPairAddition}
                  onRemovePair={handleRemoveTradingPair}
                  onApplyAllSuggestions={handleApplyAllPairSuggestions}
                />
              </div>
            )}

            {/* ✅ FILA 7: HISTORIAL */}
            {showTradeView && (
              <div className="dashboard-row full-width">
                <OrderHistory
                  refreshTrigger={refreshTrigger}
                  manualRefreshTrigger={manualRefreshTrigger}
                  symbol=""
                />
              </div>
            )}

            {/* ✅ FILA 7.5: RENDIMIENTOS */}
            {showTradeView && (
              <div className="dashboard-row full-width">
                <Rendimientos
                  refreshTrigger={refreshTrigger}
                  manualRefreshTrigger={manualRefreshTrigger}
                />
              </div>
            )}
          </>
        )}

        <AddUserModal
          open={showAddUserModal}
          onClose={() => setShowAddUserModal(false)}
          onCreate={handleCreateUser}
          loading={addUserState.loading}
          error={addUserState.error}
          success={addUserState.success}
          users={adminUsers}
          usersLoading={adminUsersLoading}
          usersError={adminUsersError}
          onResend={handleResendUser}
          onDelete={handleDeleteUser}
          busyResendEmail={adminUsersBusy.resend}
          busyDeleteEmail={adminUsersBusy.delete}
        />

        <EditUserModal
          open={Boolean(editUser)}
          user={editUser}
          onClose={() => setEditUser(null)}
          onSave={handleUpdateUser}
          loading={editUserState.loading}
          error={editUserState.error}
          success={editUserState.success}
          activationWallet={activationWallet}
          activationPionexRecipient={activationPionexRecipient}
          activationPionexUid={activationPionexUid}
          subscriptionInfo={subscriptionInfo}
          onSubscriptionConfirmed={handleSubscriptionConfirmed}
        />

        <EditUserModal
          open={showProfileModal}
          user={currentProfile ? { username: currentUser, ...currentProfile } : { username: currentUser }}
          onClose={() => {
            setShowProfileModal(false);
          }}
          onSave={handleUpdateMyProfile}
          loading={selfProfileState.loading}
          error={selfProfileState.error}
          success={selfProfileState.success}
          title={profileSetupLocked ? "Completa tu perfil" : "Mi perfil"}
          subtitle={profileSetupLocked ? "Necesitamos terminar tu configuración antes de continuar." : currentUser}
          submitLabel={profileSetupLocked ? "Guardar y continuar" : "Guardar cambios"}
          includeIdentityFields
          forceComplete={false}
          allowAdminControls={false}
          allowPromptControl
          allowPasswordChange
          preloadSensitiveFields
          activationWallet={activationWallet}
          activationPionexRecipient={activationPionexRecipient}
          activationPionexUid={activationPionexUid}
          subscriptionInfo={subscriptionInfo}
        />

        <ApprovePreregModal
          open={Boolean(approvePreregItem)}
          item={approvePreregItem}
          onClose={() => setApprovePreregItem(null)}
          onApprove={handleApprovePrereg}
          loading={approvePreregState.loading}
          error={approvePreregState.error}
          success={approvePreregState.success}
        />

        <UserLogModal
          open={Boolean(logModalUser)}
          user={logModalUser?.username}
          logLines={logModalState.lines}
          loading={logModalState.loading}
          error={logModalState.error}
          onClose={handleCloseConsole}
          onRefresh={handleRefreshConsole}
        />

        <div className="toast-container" style={{position:'fixed',top:16,right:16,zIndex:9999,display:'flex',flexDirection:'column',gap:8}}>
          {toasts.map((t) => (
            <div key={t.id} className={`toast-${t.type}`} style={{
              background: t.type==='compra' ? 'rgba(0,255,136,0.12)' : 'rgba(255,50,50,0.12)',
              border: `1px solid ${t.type==='compra' ? '#00ff88' : '#ff3232'}`,
              borderRadius: 10,
              padding: '12px 18px',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              minWidth: 280,
              boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
              animation: 'toastSlideIn 0.3s ease',
              backdropFilter: 'blur(10px)',
            }}>
               <div style={{fontSize:15,marginBottom:4}}>
                {t.type==='compra' ? '🟢 ¡COMPRA!' : '🔴 ¡VENTA!'}
                {t.type==='venta' && t.exit_type ? ` · ${String(t.exit_type).toUpperCase()}` : ''}
              </div>
              <div style={{opacity:0.9}}>
                {t.symbol} @ ${typeof t.price === 'number' ? t.price.toFixed(2) : t.price}
                {t.method ? ` [${t.method}]` : ''}
              </div>
              {t.type==='compra' && (
                <div style={{marginTop:6,fontSize:12,opacity:0.85,lineHeight:'1.6'}}>
                  {t.entry_price != null && (
                    <div>Entrada: <b style={{color:'#fff'}}>${typeof t.entry_price === 'number' ? t.entry_price.toFixed(2) : t.entry_price}</b></div>
                  )}
                  {t.sl != null && (
                    <div>SL: <b style={{color:'#ff6b6b'}}>${typeof t.sl === 'number' ? t.sl.toFixed(2) : t.sl}{t.sl_pct != null ? ` (${typeof t.sl_pct === 'number' ? t.sl_pct.toFixed(2) : t.sl_pct}%)` : ''}</b></div>
                  )}
                  {t.tp != null && (
                    <div>TP: <b style={{color:'#69db7c'}}>${typeof t.tp === 'number' ? t.tp.toFixed(2) : t.tp}{t.tp_pct != null ? ` (${typeof t.tp_pct === 'number' ? t.tp_pct.toFixed(2) : t.tp_pct}%)` : ''}</b></div>
                  )}
                  {t.hz_horas != null && t.hz_horas > 0 && (
                    <div>HZ: <b style={{color:'#74c0fc'}}>{(t.hz_horas)}h {t.hz_vencimiento ? `· ${formatHzRemaining(t.hz_vencimiento)}` : `salida por tiempo`}</b></div>
                  )}
                </div>
              )}
              {t.type==='venta' && (
                <div style={{marginTop:6,fontSize:12,opacity:0.85,lineHeight:'1.6'}}>
                  {t.entry_price != null && (
                    <div>Entrada: <b style={{color:'#fff'}}>${typeof t.entry_price === 'number' ? t.entry_price.toFixed(2) : t.entry_price}</b></div>
                  )}
                  <div>Salida: <b style={{color:'#fff'}}>${typeof t.price === 'number' ? t.price.toFixed(2) : t.price}</b></div>
                  {t.sl != null && (
                    <div>SL: <b style={{color:'#ff6b6b'}}>${typeof t.sl === 'number' ? t.sl.toFixed(2) : t.sl}{t.sl_pct != null ? ` (${typeof t.sl_pct === 'number' ? t.sl_pct.toFixed(2) : t.sl_pct}%)` : ''}</b></div>
                  )}
                  {t.tp != null && (
                    <div>TP: <b style={{color:'#69db7c'}}>${typeof t.tp === 'number' ? t.tp.toFixed(2) : t.tp}{t.tp_pct != null ? ` (${typeof t.tp_pct === 'number' ? t.tp_pct.toFixed(2) : t.tp_pct}%)` : ''}</b></div>
                  )}
                  {t.pnl != null && (
                    <div style={{marginTop:4}}>
                      P&L: <b style={{color: t.pnl >= 0 ? '#69db7c' : '#ff6b6b'}}>
                        ${typeof t.pnl === 'number' ? t.pnl.toFixed(2) : t.pnl} ({typeof t.pnl_percentage === 'number' ? t.pnl_percentage.toFixed(2) : t.pnl_percentage}%)
                      </b>
                    </div>
                  )}
                  {t.razon_venta && (
                    <div>Razón: <b style={{color:'#fff'}}>{String(t.razon_venta).toUpperCase()}</b></div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
